/**
 * CR055/CR082: Source type dropdown component
 * Uses only the 5 approved bucket-level options from lib/sourceTypes.ts
 */

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  type DropdownSourceType,
  getSourceTypeOptions,
  getSourceTypeLabel as getLabel,
  DROPDOWN_LABELS,
  normalizeSourceType,
  bucketForSourceType,
} from "@/lib/sourceTypes";

// Re-export the type for backwards compatibility
export type SourceType = DropdownSourceType;

interface SourceTypeSelectProps {
  value: string | null;
  onChange: (value: DropdownSourceType) => void;
  disabled?: boolean;
  showUsage?: boolean;
}

/**
 * Maps any canonical source type to the appropriate dropdown option
 * E.g., ontario_flyer_search -> official_flyer, fallback_distributor -> html
 */
function mapToDropdownValue(value: string | null | undefined): DropdownSourceType {
  const normalized = normalizeSourceType(value);
  const bucket = bucketForSourceType(normalized);
  
  // Map bucket to dropdown value
  switch (bucket) {
    case "official_gift_card":
      return "official_giftcard";
    case "official_flyer":
      return "official_flyer";
    case "official_home":
      return "official_home";
    case "forums":
      return "forum";
    case "affiliated_partners":
      return "html";
    case "other":
    default:
      // Default to official_giftcard if nothing else matches
      return "official_giftcard";
  }
}

export function SourceTypeSelect({ 
  value, 
  onChange, 
  disabled,
  showUsage = true,
}: SourceTypeSelectProps) {
  const options = getSourceTypeOptions();
  // Map incoming value to the appropriate dropdown option
  const dropdownValue = mapToDropdownValue(value);
  
  return (
    <Select 
      value={dropdownValue} 
      onValueChange={(v) => onChange(v as DropdownSourceType)} 
      disabled={disabled}
    >
      <SelectTrigger className="w-[200px]">
        <SelectValue placeholder="Select type" />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <TooltipProvider key={option.value}>
            <Tooltip>
              <TooltipTrigger asChild>
                <SelectItem value={option.value}>
                  {option.label}
                </SelectItem>
              </TooltipTrigger>
              {showUsage && (
                <TooltipContent side="right" className="max-w-[200px]">
                  <p className="text-xs">{option.usage}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        ))}
      </SelectContent>
    </Select>
  );
}

// Re-export for backwards compatibility
export { getLabel as getSourceTypeLabel };
